import { supabase } from '../lib/supabase';

/**
 * Testimonial Service
 * Handles all testimonial-related operations
 */

export const testimonialService = {
  /**
   * Get all testimonials for an institute
   * @param {string} instituteId - Institute user ID
   * @returns {Promise<Array>} List of testimonials
   */
  async getInstituteTestimonials(instituteId) {
    try {
      const { data, error } = await supabase?.from('testimonials')?.select('*')?.eq('institute_id', instituteId)?.order('display_order', { ascending: true })?.order('created_at', { ascending: false });

      if (error) {
        if (error?.code === 'PGRST116') {
          return [];
        }
        throw error;
      }

      return data?.map(testimonial => ({
        id: testimonial?.id,
        instituteId: testimonial?.institute_id,
        studentName: testimonial?.student_name,
        studentProgram: testimonial?.student_program,
        studentYear: testimonial?.student_year,
        rating: testimonial?.rating,
        content: testimonial?.content,
        studentAvatar: testimonial?.student_avatar,
        isFeatured: testimonial?.is_featured,
        isApproved: testimonial?.is_approved,
        displayOrder: testimonial?.display_order,
        createdAt: testimonial?.created_at,
        updatedAt: testimonial?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching institute testimonials:', error);
      throw error;
    }
  },

  /**
   * Get approved testimonials for public display
   * @param {string} instituteId - Institute user ID
   * @returns {Promise<Array>} List of approved testimonials
   */
  async getApprovedTestimonials(instituteId) {
    try {
      const { data, error } = await supabase?.from('testimonials')?.select('*')?.eq('institute_id', instituteId)?.eq('is_approved', true)?.order('is_featured', { ascending: false })?.order('display_order', { ascending: true })?.order('created_at', { ascending: false });

      if (error) {
        if (error?.code === 'PGRST116') {
          return [];
        }
        throw error;
      }

      return data?.map(testimonial => ({
        id: testimonial?.id,
        name: testimonial?.student_name,
        program: testimonial?.student_program,
        year: testimonial?.student_year,
        rating: testimonial?.rating,
        content: testimonial?.content,
        avatar: testimonial?.student_avatar,
        avatarAlt: `${testimonial?.student_name} profile picture`
      })) || [];
    } catch (error) {
      console.error('Error fetching approved testimonials:', error);
      throw error;
    }
  },

  /**
   * Create a new testimonial
   * @param {Object} testimonialData - Testimonial data
   * @returns {Promise<Object>} Created testimonial
   */
  async createTestimonial(testimonialData) {
    try {
      const { data, error } = await supabase?.from('testimonials')?.insert({
          institute_id: testimonialData?.instituteId,
          student_name: testimonialData?.studentName,
          student_program: testimonialData?.studentProgram,
          student_year: testimonialData?.studentYear,
          rating: testimonialData?.rating,
          content: testimonialData?.content,
          student_avatar: testimonialData?.studentAvatar || null,
          is_featured: testimonialData?.isFeatured || false,
          is_approved: testimonialData?.isApproved !== undefined ? testimonialData?.isApproved : true,
          display_order: testimonialData?.displayOrder || 0
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        instituteId: data?.institute_id,
        studentName: data?.student_name,
        studentProgram: data?.student_program,
        studentYear: data?.student_year,
        rating: data?.rating,
        content: data?.content,
        studentAvatar: data?.student_avatar,
        isFeatured: data?.is_featured,
        isApproved: data?.is_approved,
        displayOrder: data?.display_order,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating testimonial:', error);
      throw error;
    }
  },

  /**
   * Update an existing testimonial
   * @param {string} testimonialId - Testimonial ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated testimonial
   */
  async updateTestimonial(testimonialId, updates) {
    try {
      const updateData = {};
      
      if (updates?.studentName !== undefined) updateData.student_name = updates?.studentName;
      if (updates?.studentProgram !== undefined) updateData.student_program = updates?.studentProgram;
      if (updates?.studentYear !== undefined) updateData.student_year = updates?.studentYear;
      if (updates?.rating !== undefined) updateData.rating = updates?.rating;
      if (updates?.content !== undefined) updateData.content = updates?.content;
      if (updates?.studentAvatar !== undefined) updateData.student_avatar = updates?.studentAvatar;
      if (updates?.isFeatured !== undefined) updateData.is_featured = updates?.isFeatured;
      if (updates?.isApproved !== undefined) updateData.is_approved = updates?.isApproved;
      if (updates?.displayOrder !== undefined) updateData.display_order = updates?.displayOrder;

      const { data, error } = await supabase?.from('testimonials')?.update(updateData)?.eq('id', testimonialId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        instituteId: data?.institute_id,
        studentName: data?.student_name,
        studentProgram: data?.student_program,
        studentYear: data?.student_year,
        rating: data?.rating,
        content: data?.content,
        studentAvatar: data?.student_avatar,
        isFeatured: data?.is_featured,
        isApproved: data?.is_approved,
        displayOrder: data?.display_order,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating testimonial:', error);
      throw error;
    }
  },

  /**
   * Delete a testimonial
   * @param {string} testimonialId - Testimonial ID
   * @returns {Promise<void>}
   */
  async deleteTestimonial(testimonialId) {
    try {
      const { error } = await supabase?.from('testimonials')?.delete()?.eq('id', testimonialId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      throw error;
    }
  }
};